import { Component, HostBinding, OnInit, inject } from '@angular/core';
import { setBackendMessageTranslator } from '@core/api/ql-result.util';
import { AuthService } from '@core/services/auth.service';
import { BackendI18nService } from '@core/services/backend-i18n.service';
import { LanguageService } from '@core/services/language.service';
import { PushNotificationsService } from '@core/services/push-notifications.service';
import { ThemeService } from '@core/services/theme';
import { WorkspaceService } from '@core/services/workspace.service';
import { IonApp, IonRouterOutlet } from '@ionic/angular/standalone';

@Component({
  selector: 'app-root',
  templateUrl: 'app.component.html',
  animations: [],
  imports: [IonApp, IonRouterOutlet],
})
export class AppComponent implements OnInit {
  /**
   * CSS keyframes are flattened by the media query in styles/_animations.scss,
   * but Angular-driven animations (route transitions, the loading⇄content
   * crossfade) run through the Web Animations API and ignore it. Switching the
   * whole animation subtree off here is the documented escape hatch, and it
   * has to be read once at construction — Angular reads `@.disabled` from the
   * host, not from a live media-query subscription.
   */
  @HostBinding('@.disabled') readonly animationsDisabled =
    typeof window !== 'undefined' &&
    window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  private theme = inject(ThemeService);
  private language = inject(LanguageService);
  private auth = inject(AuthService);
  private ws = inject(WorkspaceService);
  private backendI18n = inject(BackendI18nService);
  private push = inject(PushNotificationsService);

  async ngOnInit(): Promise<void> {
    // Bind before theme/boot work so a killed-app tray tap is queued rather
    // than dropped while Capacitor still holds the launch extras.
    await this.push.listenForTaps();
    // Let the plain API helpers localize backend `error_message` / `success_message`
    // text through the ALERT bucket without taking a service dependency.
    setBackendMessageTranslator((message) =>
      this.backendI18n.translateApiMessage(message),
    );

    // Drop any leftover demo-auth state before the first guarded route runs.
    this.auth.bootClean();
    if (!this.auth.hasToken()) {
      this.ws.clear();
    }

    // Boot language: `?lang=` or the last choice on this device. Once a session
    // loads, AuthService hands the account preference to the same service.
    this.language.initFromLocal();
    await this.theme.init();
  }
}
